import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Search, Check, X, Trash2, Pencil, Eye, AlertCircle, Gift, Truck } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Th, Td, Tile } from "../components/admin/AdminTable";
import { EditClubPanel } from "../components/admin/EditClubPanel";
import { OrderDetail } from "../components/admin/OrderDetail";
import { RewardDetail } from "../components/admin/RewardDetail";
import { UserModal } from "../components/admin/UserModal";
import { AdminModal } from "../components/admin/AdminModal";

const TABS = [
    { id: "stats", label: "Statistiques" },
    { id: "moderation", label: "Modération" },
    { id: "users", label: "Utilisateurs" },
    { id: "clubs", label: "Clubs" },
    { id: "orders", label: "Commandes" },
    { id: "rewards", label: "Récompenses" },
    { id: "config", label: "Config" },
];

// Recharts config — module-level constants to avoid creating new refs each render
const CHART_MARGIN = { top: 10, right: 20, left: -10, bottom: 0 };
const CHART_TICK = { fontSize: 11 };
const CHART_LEGEND_STYLE = { fontSize: 12 };
const CHART_TOOLTIP_STYLE = { background: "#0a0a0a", border: "1px solid #27272a", color: "#fafafa", fontSize: 12 };
const CHART_TOOLTIP_LABEL_STYLE = { color: "#a1a1aa" };
const CHART_DOT = { r: 3 };

// Order status badge helpers
const ORDER_STATUS_LABELS = {
    ready: "À VALIDER",
    validated: "VALIDÉ",
    in_production: "EN PRODUCTION",
    delivered: "LIVRÉ",
    collecting: "COLLECTE",
};
const ORDER_STATUS_CLS = {
    delivered: "border-green-700 text-green-400",
    in_production: "border-orange-500/40 text-orange-500",
    ready: "border-yellow-500/60 text-yellow-400 bg-yellow-500/10",
    validated: "border-blue-500/40 text-blue-400",
};
function orderStatusLabel(s) { return ORDER_STATUS_LABELS[s] || s; }
function orderStatusCls(s) { return ORDER_STATUS_CLS[s] || "border-zinc-700 text-zinc-300"; }

export default function DashboardAdmin() {
    const [tab, setTab] = useState("stats");
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [pendingSponsors, setPendingSponsors] = useState(0);
    const [pendingRewards, setPendingRewards] = useState(0);

    const refreshCounts = async () => {
        const [c1, c2, c3] = await Promise.all([
            api.get("/admin/clubs"),
            api.get("/admin/users", { params: { role: "sponsor", status: "pending" } }),
            api.get("/admin/rewards", { params: { status: "pending" } }),
        ]);
        setCounts(c1.data.counts || {});
        setPendingSponsors(c2.data.counts?.pending_sponsors || 0);
        setPendingRewards(c3.data.counts?.pending || 0);
    };

    useEffect(() => {
        refreshCounts();
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-8">
            <header>
                <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                    Back-office
                </div>
                <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
                    Administration
                </h1>
            </header>

            <div className="flex flex-wrap gap-px bg-zinc-800 border border-zinc-800">
                {TABS.map((t) => {
                    const badge =
                        t.id === "moderation" ? counts.pending :
                        t.id === "users" ? pendingSponsors :
                        t.id === "rewards" ? pendingRewards : 0;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={[
                                "px-6 py-3 text-xs uppercase tracking-widest font-bold transition-colors inline-flex items-center gap-2",
                                tab === t.id
                                    ? "bg-gradient-to-r from-orange-600 to-red-600 text-white"
                                    : "bg-zinc-950 text-zinc-400 hover:text-white",
                            ].join(" ")}
                        >
                            {t.label}
                            {badge > 0 && (
                                <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center">
                                    {badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {tab === "stats" && <Stats />}
            {tab === "moderation" && <Moderation onChange={refreshCounts} />}
            {tab === "users" && <Users onChange={refreshCounts} />}
            {tab === "clubs" && <Clubs onChange={refreshCounts} />}
            {tab === "orders" && <Orders />}
            {tab === "rewards" && <Rewards onChange={refreshCounts} />}
            {tab === "config" && <Config />}
        </div>
    );
}

function Stats() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then((r) => setData(r.data));
    }, []);
    if (!data) return <div className="text-zinc-500">Chargement…</div>;

    const sections = [
        { key: "clubs", label: "Annonces clubs", icon: "🏟️" },
        { key: "sponsors", label: "Sponsors", icon: "🤝" },
        { key: "sponsorships", label: "Annonces sponsoring", icon: "📣" },
    ];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
                <Tile label="Total clubs" value={data.totals.clubs} />
                <Tile label="Approuvés" value={data.totals.approved_clubs} />
                <Tile label="Total sponsors" value={data.totals.sponsors} />
                <Tile label="Validés" value={data.totals.approved_sponsors} />
                <Tile label="Sponsorings" value={data.totals.sponsorships} />
            </div>

            {data.timeseries && data.timeseries.length > 0 && (
                <div className="card-tu" data-testid="stats-monthly-chart">
                    <div className="flex items-center gap-2 mb-4">
                        <h3 className="font-display text-2xl uppercase tracking-tight">Évolution mensuelle (12 derniers mois)</h3>
                    </div>
                    <div style={{ width: "100%", height: 320 }}>
                        <ResponsiveContainer>
                            <LineChart data={data.timeseries} margin={CHART_MARGIN}>
                                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                                <XAxis dataKey="month" stroke="#71717a" tick={CHART_TICK} />
                                <YAxis stroke="#71717a" tick={CHART_TICK} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={CHART_TOOLTIP_STYLE}
                                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                                />
                                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                                <Line type="monotone" dataKey="clubs" name="Clubs inscrits" stroke="#f97316" strokeWidth={2} dot={CHART_DOT} />
                                <Line type="monotone" dataKey="sponsors" name="Sponsors" stroke="#22c55e" strokeWidth={2} dot={CHART_DOT} />
                                <Line type="monotone" dataKey="sponsorships" name="Annonces sponsoring" stroke="#06b6d4" strokeWidth={2} dot={CHART_DOT} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {sections.map((s) => {
                const d = data[s.key];
                return (
                    <div key={s.key} className="card-tu">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-2xl">{s.icon}</span>
                            <h3 className="font-display text-2xl uppercase tracking-tight">{s.label}</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
                            <Tile label={`Mois en cours · ${data.current_month}`} value={d.current_month} />
                            <Tile label={`Mois précédent · ${data.previous_month}`} value={d.previous_month} />
                            <Tile label={`Année ${data.year}`} value={d.year} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Moderation({ onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        setLoading(true);
        const { data } = await api.get("/admin/clubs", { params: { status: "pending" } });
        setItems(data.items || []);
        setLoading(false);
        onChange?.();
    };

    useEffect(() => { reload(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.post(`/admin/clubs/${id}/status?status=${status}`);
            toast.success(status === "approved" ? "Club approuvé" : "Club rejeté");
            reload();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    if (editing) return <EditClubPanel club={editing} onClose={() => { setEditing(null); reload(); }} />;

    return (
        <div className="space-y-4">
            <div className="card-tu border-l-4 border-l-orange-500 flex items-start gap-3">
                <AlertCircle className="text-orange-500 shrink-0" />
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                        File de modération
                    </div>
                    <p className="text-zinc-300 text-sm">
                        Approuvez ou refusez les clubs en attente. Une fois approuvés, ils deviennent
                        visibles publiquement et peuvent recevoir des sponsors.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="text-zinc-500">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="card-tu text-center py-16 text-zinc-400">
                    <Check size={28} className="text-green-500 mx-auto mb-3" />
                    Aucune fiche en attente. Tout est en ordre !
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`pending-club-${c.id}`}
                            className="card-tu flex flex-wrap items-center gap-4"
                        >
                            <div className="flex-1 min-w-[280px]">
                                <div className="font-display text-3xl uppercase tracking-tight">
                                    {c.name}
                                </div>
                                <div className="text-xs text-zinc-500 uppercase tracking-widest">
                                    {c.sport} · {c.city} · {c.department} · {c.level}
                                </div>
                                <div className="text-xs text-zinc-400 mt-1">
                                    {c.num_players} joueurs · {c.num_teams} équipes
                                </div>
                                {c.needs && (
                                    <div className="text-sm text-zinc-300 mt-2 italic">"{c.needs}"</div>
                                )}
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {(c.teams_by_category || []).map((t) => (
                                        <span key={t.age_category} className="chip border-zinc-700 text-zinc-300">
                                            {t.age_category}: {t.num_teams}×{t.players_per_team}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    data-testid={`view-${c.id}`}
                                    onClick={() => setEditing(c)}
                                    className="btn-ghost py-2 px-4 text-xs"
                                >
                                    <Eye size={14} /> Détails
                                </button>
                                <button
                                    data-testid={`approve-${c.id}`}
                                    onClick={() => setStatus(c.id, "approved")}
                                    className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                                >
                                    <Check size={14} /> Approuver
                                </button>
                                <button
                                    data-testid={`reject-${c.id}`}
                                    onClick={() => setStatus(c.id, "rejected")}
                                    className="px-4 py-2 border border-red-900 hover:bg-red-950 hover:border-red-500 text-red-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                                >
                                    <X size={14} /> Refuser
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Users({ onChange }) {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [q, setQ] = useState("");
    const [role, setRole] = useState("");
    const [status, setStatus] = useState("");
    const [modal, setModal] = useState(null); // null | "new" | user

    const reload = async () => {
        const params = {};
        if (q) params.q = q;
        if (role) params.role = role;
        if (status) params.status = status;
        const { data } = await api.get("/admin/users", { params });
        setItems(data.items || []);
        setCounts(data.counts || {});
        onChange?.();
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [q, role, status]);

    const setUserStatus = async (uid, st, e) => {
        e?.stopPropagation();
        try {
            await api.post(`/admin/users/${uid}/status?status=${st}`);
            toast.success("Statut mis à jour");
            reload();
        } catch (e2) { toast.error(formatErr(e2.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[260px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="users-search"
                        placeholder="Rechercher par nom ou email…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="input-tu pl-9"
                    />
                </div>
                <div className="flex gap-px bg-zinc-800 border border-zinc-800">
                    {[
                        { v: "", l: `Tous (${counts.total || 0})` },
                        { v: "sponsor", l: `Sponsors (${counts.sponsors || 0})` },
                        { v: "club", l: `Clubs (${counts.clubs || 0})` },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setRole(f.v)}
                            data-testid={`users-role-${f.v || "all"}`}
                            className={[
                                "px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors",
                                role === f.v
                                    ? "bg-gradient-to-r from-orange-600 to-red-600 text-white"
                                    : "bg-zinc-950 text-zinc-400 hover:text-white",
                            ].join(" ")}
                        >{f.l}</button>
                    ))}
                </div>
                <select
                    data-testid="users-status-filter"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="input-tu w-auto"
                >
                    <option value="">Tous statuts</option>
                    <option value="pending">En attente</option>
                    <option value="approved">Approuvés</option>
                    <option value="rejected">Rejetés</option>
                </select>
                <button
                    data-testid="users-create"
                    onClick={() => setModal("new")}
                    className="btn-primary py-2 px-4 text-xs"
                >
                    + Nouvel utilisateur
                </button>
            </div>

            {counts.pending_sponsors > 0 && (
                <div className="card-tu border-l-4 border-l-orange-500">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                        {counts.pending_sponsors} sponsor(s) en attente
                    </div>
                    <p className="text-zinc-300 text-sm">
                        Vérifiez le SIREN et approuvez ou refusez chaque inscription.
                    </p>
                </div>
            )}

            <div className="card-tu overflow-x-auto p-0">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                            <Th>Email</Th>
                            <Th>Nom</Th>
                            <Th>Rôle</Th>
                            <Th>Téléphone</Th>
                            <Th>SIREN</Th>
                            <Th>Statut</Th>
                            <Th>Activité</Th>
                            <Th>Points</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-zinc-500 py-12">Aucun utilisateur.</td></tr>
                        ) : items.map((u) => (
                            <tr
                                key={u.id}
                                data-testid={`user-row-${u.id}`}
                                onClick={() => setModal(u)}
                                className={[
                                    "border-b border-zinc-900 cursor-pointer hover:bg-zinc-900/30",
                                    u.role === "sponsor" && u.status === "pending" ? "bg-orange-950/10" : "",
                                ].join(" ")}
                            >
                                <Td>{u.email}</Td>
                                <Td className="font-bold text-orange-500 underline-offset-2 hover:underline" data-testid={`user-name-${u.id}`}>{u.name}</Td>
                                <Td><span className="chip border-orange-500/40 text-orange-500">{u.role}</span></Td>
                                <Td className="font-mono text-xs" data-testid={`user-phone-${u.id}`}>{u.phone || "—"}</Td>
                                <Td className="font-mono text-xs">{u.siren || "—"}</Td>
                                <Td>
                                    {u.role !== "admin" && (
                                        <span className={[
                                            "chip",
                                            u.status === "approved" ? "border-green-700 text-green-400" :
                                            u.status === "pending" ? "border-orange-500/40 text-orange-500" :
                                            u.status === "rejected" ? "border-red-900 text-red-400" :
                                            "border-zinc-700 text-zinc-300",
                                        ].join(" ")}>{u.status || "—"}</span>
                                    )}
                                </Td>
                                <Td className="text-xs text-zinc-400">
                                    {u.role === "sponsor" && `${u.sponsorships_count || 0} sponsoring${(u.sponsorships_count || 0) > 1 ? "s" : ""}`}
                                    {u.role === "club" && `${u.clubs_count || 0} annonce${(u.clubs_count || 0) > 1 ? "s" : ""}`}
                                </Td>
                                <Td className="text-orange-500 font-bold">{u.loyalty_points || 0}</Td>
                                <Td>
                                    {u.role === "sponsor" && u.status !== "approved" && (
                                        <button
                                            data-testid={`user-approve-${u.id}`}
                                            onClick={(e) => setUserStatus(u.id, "approved", e)}
                                            className="text-xs uppercase tracking-widest text-green-400 hover:underline"
                                        >Approuver</button>
                                    )}
                                    {u.role === "sponsor" && u.status !== "rejected" && (
                                        <button
                                            data-testid={`user-reject-${u.id}`}
                                            onClick={(e) => setUserStatus(u.id, "rejected", e)}
                                            className="text-xs uppercase tracking-widest text-red-400 hover:underline ml-2"
                                        >Rejeter</button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {modal && (
                <UserModal
                    user={modal === "new" ? null : modal}
                    onClose={() => setModal(null)}
                    onSaved={reload}
                />
            )}
        </div>
    );
}

function Clubs({ onChange }) {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const params = {};
        if (q) params.q = q;
        if (filter) params.status = filter;
        const { data } = await api.get("/admin/clubs", { params });
        setItems(data.items || []);
        setCounts(data.counts || {});
        onChange?.();
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [q, filter]);

    const setStatus = async (id, status) => {
        try {
            await api.post(`/admin/clubs/${id}/status?status=${status}`);
            toast.success("Statut mis à jour");
            reload();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Supprimer ${c.name} ? Cette action est irréversible.`)) return;
        try {
            await api.delete(`/admin/clubs/${c.id}`);
            toast.success("Club supprimé");
            reload();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    if (editing) return (
        <AdminModal onClose={() => { setEditing(null); reload(); }} testid="club-modal" maxWidth="max-w-3xl">
            <EditClubPanel club={editing} onClose={() => { setEditing(null); reload(); }} />
        </AdminModal>
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[260px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="admin-clubs-search"
                        placeholder="Rechercher par nom ou ville…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="input-tu pl-9"
                    />
                </div>
                <div className="flex gap-px bg-zinc-800 border border-zinc-800">
                    {[
                        { v: "", l: `Tous (${(counts.pending||0)+(counts.approved||0)+(counts.rejected||0)})` },
                        { v: "pending", l: `En attente (${counts.pending || 0})` },
                        { v: "approved", l: `Approuvés (${counts.approved || 0})` },
                        { v: "rejected", l: `Rejetés (${counts.rejected || 0})` },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setFilter(f.v)}
                            data-testid={`admin-clubs-filter-${f.v || "all"}`}
                            className={[
                                "px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors",
                                filter === f.v
                                    ? "bg-gradient-to-r from-orange-600 to-red-600 text-white"
                                    : "bg-zinc-950 text-zinc-400 hover:text-white",
                            ].join(" ")}
                        >{f.l}</button>
                    ))}
                </div>
            </div>

            <div className="card-tu overflow-x-auto p-0">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                            <Th>Nom</Th>
                            <Th>Sport</Th>
                            <Th>Ville</Th>
                            <Th>Effectif</Th>
                            <Th>Statut</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-zinc-500 py-12">Aucun club.</td></tr>
                        ) : items.map((c) => (
                            <tr
                                key={c.id}
                                data-testid={`admin-club-row-${c.id}`}
                                onClick={() => setEditing(c)}
                                className={[
                                    "border-b border-zinc-900 hover:bg-zinc-950/50 transition-colors cursor-pointer",
                                    c.status === "pending" ? "bg-orange-950/10" : "",
                                ].join(" ")}
                            >
                                <Td className="font-bold text-orange-500 underline-offset-2 hover:underline" data-testid={`admin-club-name-${c.id}`}>{c.name}</Td>
                                <Td>{c.sport}</Td>
                                <Td>{c.city}</Td>
                                <Td>{c.num_players}j · {c.num_teams}é</Td>
                                <Td>
                                    <span className={[
                                        "chip",
                                        c.status === "approved" ? "border-green-700 text-green-400" :
                                        c.status === "pending" ? "border-orange-500/40 text-orange-500" :
                                        "border-red-900 text-red-400"
                                    ].join(" ")}>{c.status}</span>
                                </Td>
                                <Td>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                                            data-testid={`edit-${c.id}`}
                                            className="text-xs uppercase tracking-widest text-zinc-300 hover:text-orange-500 inline-flex items-center gap-1"
                                        >
                                            <Pencil size={12} /> Modifier
                                        </button>
                                        {c.status !== "approved" && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setStatus(c.id, "approved"); }}
                                                data-testid={`row-approve-${c.id}`}
                                                className="text-xs uppercase tracking-widest text-green-400 hover:underline inline-flex items-center gap-1"
                                            >
                                                <Check size={12} /> Approuver
                                            </button>
                                        )}
                                        {c.status !== "rejected" && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setStatus(c.id, "rejected"); }}
                                                data-testid={`row-reject-${c.id}`}
                                                className="text-xs uppercase tracking-widest text-red-400 hover:underline inline-flex items-center gap-1"
                                            >
                                                <X size={12} /> Rejeter
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); remove(c); }}
                                            data-testid={`delete-${c.id}`}
                                            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-red-500 inline-flex items-center gap-1"
                                        >
                                            <Trash2 size={12} /> Supprimer
                                        </button>
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Orders() {
    const [items, setItems] = useState([]);
    const [view, setView] = useState("active"); // active | archives
    const [statusFilter, setStatusFilter] = useState("all"); // all | ready | validated | in_production | collecting
    const [open, setOpen] = useState(null);

    const reload = () => api.get("/admin/orders").then((r) => setItems(r.data.items || []));
    useEffect(() => { reload(); }, []);

    // Sort priority: ready (à valider) > validated > in_production > collecting > delivered
    const STATUS_PRIORITY = { ready: 0, validated: 1, in_production: 2, collecting: 3, delivered: 4 };

    const base = items.filter((o) =>
        view === "archives" ? o.jersey.status === "delivered" : o.jersey.status !== "delivered"
    );
    const filtered = base
        .filter((o) => statusFilter === "all" || o.jersey.status === statusFilter)
        .sort((a, b) => (STATUS_PRIORITY[a.jersey.status] ?? 99) - (STATUS_PRIORITY[b.jersey.status] ?? 99));

    const counts = {
        ready: base.filter((o) => o.jersey.status === "ready").length,
        validated: base.filter((o) => o.jersey.status === "validated").length,
        in_production: base.filter((o) => o.jersey.status === "in_production").length,
        collecting: base.filter((o) => o.jersey.status === "collecting").length,
        delivered: items.filter((o) => o.jersey.status === "delivered").length,
    };

    const exportCsv = () => {
        const rows = [["Club", "Catégorie", "Sponsors", "Statut"]];
        filtered.forEach((o) => rows.push([
            o.club?.name, o.jersey?.age_category,
            o.sponsorships?.map((s) => s.sponsor_company).join("; "),
            o.jersey?.status,
        ]));
        const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `sponsormania-${view}.csv`; a.click();
        URL.revokeObjectURL(url);
    };
    const mark = async (id, action) => {
        try { await api.post(`/admin/orders/${id}/${action}`); toast.success("OK"); reload(); }
        catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    if (open) return (
        <AdminModal onClose={() => { setOpen(null); reload(); }} testid="order-modal" maxWidth="max-w-3xl">
            <OrderDetail order={open} onBack={() => { setOpen(null); reload(); }} onAction={mark} />
        </AdminModal>
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-px bg-zinc-800 border border-zinc-800">
                    {[
                        { v: "active", l: `Actives (${items.filter(o => o.jersey.status !== "delivered").length})` },
                        { v: "archives", l: `Archives (${items.filter(o => o.jersey.status === "delivered").length})` },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => { setView(f.v); setStatusFilter("all"); }}
                            data-testid={`orders-view-${f.v}`}
                            className={[
                                "px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors",
                                view === f.v
                                    ? "bg-gradient-to-r from-orange-600 to-red-600 text-white"
                                    : "bg-zinc-950 text-zinc-400 hover:text-white",
                            ].join(" ")}
                        >{f.l}</button>
                    ))}
                </div>
                <button onClick={exportCsv} data-testid="export-csv" className="btn-ghost py-2 px-4 text-xs">Exporter CSV</button>
            </div>

            {view === "active" && (
                <div className="flex flex-wrap gap-2" data-testid="orders-status-filter">
                    {[
                        { v: "all", l: `Tous (${counts.ready + counts.validated + counts.in_production + counts.collecting})`, cls: "" },
                        { v: "ready", l: `À valider (${counts.ready})`, cls: "border-yellow-500/60 text-yellow-400" },
                        { v: "validated", l: `Validés (${counts.validated})`, cls: "border-blue-500/40 text-blue-400" },
                        { v: "in_production", l: `En production (${counts.in_production})`, cls: "border-orange-500/40 text-orange-500" },
                        { v: "collecting", l: `Collecte (${counts.collecting})`, cls: "border-zinc-700 text-zinc-300" },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setStatusFilter(f.v)}
                            data-testid={`orders-filter-${f.v}`}
                            className={[
                                "chip transition-all",
                                statusFilter === f.v
                                    ? "bg-gradient-to-r from-orange-600 to-red-600 text-white border-transparent"
                                    : `bg-zinc-950 hover:bg-zinc-900 ${f.cls || "border-zinc-800 text-zinc-400"}`,
                            ].join(" ")}
                        >{f.l}</button>
                    ))}
                </div>
            )}
            {filtered.length === 0 ? (
                <div className="card-tu text-center text-zinc-500 py-12">
                    {view === "archives" ? "Aucune commande livrée." : "Aucune commande active."}
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((o) => (
                        <button
                            key={o.jersey.id}
                            onClick={() => setOpen(o)}
                            data-testid={`order-row-${o.jersey.id}`}
                            className="card-tu w-full text-left hover:border-orange-500/50 transition-colors flex flex-wrap items-center gap-4"
                        >
                            <div className="flex-1 min-w-[260px]">
                                <div className="font-display text-2xl uppercase">{o.club?.name}</div>
                                <div className="text-xs uppercase tracking-widest text-zinc-500">
                                    Maillot {o.jersey.age_category} · {o.club?.city}
                                </div>
                            </div>
                            <div className="text-xs text-zinc-400">
                                {o.sponsorships?.length || 0} sponsor{(o.sponsorships?.length || 0) > 1 ? "s" : ""}
                            </div>
                            <span className={`chip ${orderStatusCls(o.jersey.status)}`}>
                                {orderStatusLabel(o.jersey.status)}
                            </span>
                            <span className="text-xs uppercase tracking-widest text-orange-500">
                                Voir détails →
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Config() {
    const [prices, setPrices] = useState({});
    useEffect(() => {
        api.get("/admin/config").then((r) => setPrices(r.data.zone_prices || {}));
    }, []);
    const save = async () => {
        try { await api.post("/admin/config", prices); toast.success("Prix mis à jour"); }
        catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };
    return (
        <div className="space-y-8">
            <div className="card-tu max-w-2xl">
                <h3 className="font-display text-3xl uppercase tracking-tight mb-4">Tarifs des emplacements</h3>
                <div className="space-y-3">
                    {Object.entries(prices).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-4">
                            <label className="w-32 text-xs uppercase tracking-widest text-zinc-400 font-bold">{k}</label>
                            <input
                                type="number" min={0} value={v}
                                onChange={(e) => setPrices((p) => ({ ...p, [k]: +e.target.value }))}
                                className="input-tu flex-1" data-testid={`price-${k}`}
                            />
                            <span className="text-orange-500 font-bold">€</span>
                        </div>
                    ))}
                </div>
                <button onClick={save} data-testid="save-prices" className="btn-primary w-full mt-6">Enregistrer</button>
            </div>

            <BankDetailsManager />
            <RewardCatalogManager />
            <RealizationsManager />
            <TestimonialsManager />
        </div>
    );
}

function BankDetailsManager() {
    const [f, setF] = useState({ beneficiary: "", iban: "", bic: "", bank_name: "", reference_prefix: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/payment/bank").then((r) => setF(r.data || {}));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/bank", f);
            toast.success("Coordonnées bancaires mises à jour");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <div className="card-tu max-w-2xl">
            <h3 className="font-display text-3xl uppercase tracking-tight mb-1">Paiement — compte BOC</h3>
            <p className="text-xs text-zinc-500 mb-4">Coordonnées affichées aux sponsors sur la page de paiement par virement.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="label-tu">Bénéficiaire</label>
                    <input data-testid="bank-beneficiary-input" value={f.beneficiary || ""} onChange={(e) => setF({ ...f, beneficiary: e.target.value })} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Nom de la banque</label>
                    <input data-testid="bank-name-input" value={f.bank_name || ""} onChange={(e) => setF({ ...f, bank_name: e.target.value })} className="input-tu" />
                </div>
                <div className="md:col-span-2">
                    <label className="label-tu">IBAN</label>
                    <input data-testid="bank-iban-input" value={f.iban || ""} onChange={(e) => setF({ ...f, iban: e.target.value })} className="input-tu font-mono" placeholder="FR76 …" />
                </div>
                <div>
                    <label className="label-tu">BIC / SWIFT</label>
                    <input data-testid="bank-bic-input" value={f.bic || ""} onChange={(e) => setF({ ...f, bic: e.target.value })} className="input-tu font-mono" />
                </div>
                <div>
                    <label className="label-tu">Préfixe de référence</label>
                    <input data-testid="bank-ref-input" value={f.reference_prefix || ""} onChange={(e) => setF({ ...f, reference_prefix: e.target.value })} className="input-tu" placeholder="SPONSO" />
                </div>
            </div>
            <button data-testid="bank-save" onClick={save} disabled={saving} className="btn-primary w-full mt-5">
                {saving ? "…" : "Enregistrer"}
            </button>
        </div>
    );
}

function RealizationsManager() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const { data } = await api.get("/admin/realizations");
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, []);

    const remove = async (rid) => {
        if (!window.confirm("Supprimer cette réalisation ?")) return;
        try { await api.delete(`/admin/realizations/${rid}`); toast.success("Supprimée"); reload(); }
        catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="card-tu">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-3xl uppercase tracking-tight">Galerie "Nos réalisations"</h3>
                    <p className="text-xs text-zinc-500 mt-1">
                        Photos publiées sur la page publique <code>/realisations</code>.
                    </p>
                </div>
                <button data-testid="realization-add" onClick={() => setEditing("new")} className="btn-primary py-2 px-4 text-xs">
                    + Ajouter
                </button>
            </div>
            {items.length === 0 ? (
                <div className="text-zinc-500 text-sm italic">Aucune réalisation.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((r) => (
                        <div key={r.id} data-testid={`realization-item-${r.id}`} className="border border-zinc-800 bg-zinc-950 p-3 flex gap-3">
                            {r.image_path ? (
                                <img src={fileUrl(r.image_path)} alt={r.title} className="w-24 h-24 object-cover border border-zinc-800 shrink-0" />
                            ) : (
                                <div className="w-24 h-24 border border-zinc-800 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="font-bold text-sm truncate">{r.title}</div>
                                    {!r.published && <span className="chip border-zinc-700 text-zinc-500">Brouillon</span>}
                                </div>
                                {r.description && (
                                    <div className="text-[11px] text-zinc-500 line-clamp-3 mt-1">{r.description}</div>
                                )}
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => setEditing(r)} data-testid={`realization-edit-${r.id}`} className="text-[11px] uppercase tracking-widest text-orange-500 hover:underline">
                                        <Pencil size={12} className="inline" /> Modifier
                                    </button>
                                    <button onClick={() => remove(r.id)} data-testid={`realization-delete-${r.id}`} className="text-[11px] uppercase tracking-widest text-red-400 hover:underline">
                                        <Trash2 size={12} className="inline" /> Supprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {editing && (
                <RealizationEditor
                    item={editing === "new" ? null : editing}
                    onClose={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}

function RealizationEditor({ item, onClose }) {
    const [title, setTitle] = useState(item?.title || "");
    const [description, setDescription] = useState(item?.description || "");
    const [imagePath, setImagePath] = useState(item?.image_path || "");
    const [published, setPublished] = useState(item?.published ?? true);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImagePath(data.path);
            toast.success("Image téléchargée");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setUploading(false); }
    };

    const save = async () => {
        if (!title.trim()) { toast.error("Titre requis"); return; }
        if (!imagePath) { toast.error("Image requise"); return; }
        setSaving(true);
        try {
            const payload = { title, description, image_path: imagePath, published };
            if (item) await api.put(`/admin/realizations/${item.id}`, payload);
            else await api.post("/admin/realizations", payload);
            toast.success("Enregistré");
            onClose();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="realization-editor">
            <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-display text-2xl uppercase tracking-tight">
                    {item ? "Modifier" : "Nouvelle"} réalisation
                </h3>
                <div>
                    <label className="label-tu">Titre</label>
                    <input data-testid="realization-title" value={title} onChange={(e) => setTitle(e.target.value)} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Description</label>
                    <textarea data-testid="realization-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Image</label>
                    <div className="flex items-center gap-3">
                        {imagePath ? (
                            <img src={fileUrl(imagePath)} alt="" className="w-24 h-24 object-cover border border-zinc-800" />
                        ) : (
                            <div className="w-24 h-24 border border-dashed border-zinc-700" />
                        )}
                        <label className="btn-ghost py-2 px-4 text-xs cursor-pointer">
                            {uploading ? "Téléchargement…" : "Choisir un fichier"}
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                data-testid="realization-image-upload"
                                onChange={(e) => upload(e.target.files?.[0])}
                            />
                        </label>
                    </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input data-testid="realization-published" type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
                    Publier (visible sur le site public)
                </label>
                <div className="flex gap-2 pt-2">
                    <button data-testid="realization-save" onClick={save} disabled={saving} className="btn-primary flex-1 py-2 text-sm">
                        {saving ? "…" : "Enregistrer"}
                    </button>
                    <button onClick={onClose} className="btn-ghost py-2 px-4 text-sm">Annuler</button>
                </div>
            </div>
        </div>
    );
}

function TestimonialsManager() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | item

    const reload = async () => {
        const { data } = await api.get("/admin/testimonials");
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, []);

    const remove = async (tid) => {
        if (!window.confirm("Supprimer ce témoignage ?")) return;
        try { await api.delete(`/admin/testimonials/${tid}`); toast.success("Supprimé"); reload(); }
        catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="card-tu">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-3xl uppercase tracking-tight">Témoignages page Impact</h3>
                    <p className="text-xs text-zinc-500 mt-1">
                        3 témoignages sont affichés aléatoirement sur la page publique <code>/impact</code>.
                    </p>
                </div>
                <button data-testid="testimonial-add" onClick={() => setEditing("new")} className="btn-primary py-2 px-4 text-xs">
                    + Ajouter
                </button>
            </div>
            {items.length === 0 ? (
                <div className="text-zinc-500 text-sm italic">Aucun témoignage.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map((t) => (
                        <div key={t.id} data-testid={`testimonial-item-${t.id}`} className="border border-zinc-800 bg-zinc-950 p-3">
                            <div className="flex items-start justify-between gap-2">
                                <div className="font-bold text-sm">{t.author}</div>
                                {!t.published && <span className="chip border-zinc-700 text-zinc-500">Brouillon</span>}
                            </div>
                            {t.role && <div className="text-[11px] text-zinc-500 uppercase tracking-widest mt-1">{t.role}</div>}
                            <blockquote className="text-xs text-zinc-300 italic mt-2 line-clamp-3">"{t.quote}"</blockquote>
                            <div className="flex gap-2 mt-2">
                                <button onClick={() => setEditing(t)} data-testid={`testimonial-edit-${t.id}`} className="text-[11px] uppercase tracking-widest text-orange-500 hover:underline">
                                    <Pencil size={12} className="inline" /> Modifier
                                </button>
                                <button onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`} className="text-[11px] uppercase tracking-widest text-red-400 hover:underline">
                                    <Trash2 size={12} className="inline" /> Supprimer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {editing && (
                <TestimonialEditor
                    item={editing === "new" ? null : editing}
                    onClose={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}

function TestimonialEditor({ item, onClose }) {
    const [quote, setQuote] = useState(item?.quote || "");
    const [author, setAuthor] = useState(item?.author || "");
    const [role, setRole] = useState(item?.role || "");
    const [published, setPublished] = useState(item?.published ?? true);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!quote.trim()) { toast.error("Citation requise"); return; }
        if (!author.trim()) { toast.error("Nom de l'auteur requis"); return; }
        setSaving(true);
        try {
            const payload = { quote, author, role, published };
            if (item) await api.put(`/admin/testimonials/${item.id}`, payload);
            else await api.post("/admin/testimonials", payload);
            toast.success("Enregistré");
            onClose();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="testimonial-editor">
            <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-display text-2xl uppercase tracking-tight">
                    {item ? "Modifier" : "Nouveau"} témoignage
                </h3>
                <div>
                    <label className="label-tu">Citation</label>
                    <textarea data-testid="testimonial-quote" rows={4} value={quote} onChange={(e) => setQuote(e.target.value)} className="input-tu" placeholder="Texte du témoignage…" />
                </div>
                <div>
                    <label className="label-tu">Auteur (nom)</label>
                    <input data-testid="testimonial-author" value={author} onChange={(e) => setAuthor(e.target.value)} className="input-tu" placeholder="Ex : Karim B." />
                </div>
                <div>
                    <label className="label-tu">Club ou entreprise</label>
                    <input data-testid="testimonial-role" value={role} onChange={(e) => setRole(e.target.value)} className="input-tu" placeholder="Ex : Président · FC Lyon Étoile" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input data-testid="testimonial-published" type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
                    Publier (visible aléatoirement sur la page Impact)
                </label>
                <div className="flex gap-2 pt-2">
                    <button data-testid="testimonial-save" onClick={save} disabled={saving} className="btn-primary flex-1 py-2 text-sm">
                        {saving ? "…" : "Enregistrer"}
                    </button>
                    <button onClick={onClose} className="btn-ghost py-2 px-4 text-sm">Annuler</button>
                </div>
            </div>
        </div>
    );
}

function RewardCatalogManager() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | item

    const reload = async () => {
        const { data } = await api.get("/admin/reward-catalog");
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, []);

    const remove = async (rid) => {
        if (!window.confirm("Supprimer cette récompense du catalogue ?")) return;
        try { await api.delete(`/admin/reward-catalog/${rid}`); toast.success("Supprimée"); reload(); }
        catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="card-tu">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-3xl uppercase tracking-tight">Catalogue des récompenses</h3>
                    <p className="text-xs text-zinc-500 mt-1">
                        Les clubs dépensent leurs points dans ce catalogue. Image conseillée : 200 × 200 px.
                    </p>
                </div>
                <button
                    data-testid="reward-catalog-add"
                    onClick={() => setEditing("new")}
                    className="btn-primary py-2 px-4 text-xs"
                >
                    + Ajouter
                </button>
            </div>

            {items.length === 0 ? (
                <div className="text-zinc-500 text-sm italic">Aucune récompense au catalogue.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((r) => (
                        <div key={r.id} data-testid={`reward-catalog-item-${r.id}`} className="border border-zinc-800 bg-zinc-950 p-3 flex gap-3">
                            {r.image_path ? (
                                <img src={fileUrl(r.image_path)} alt={r.name} className="w-20 h-20 object-cover border border-zinc-800 shrink-0" />
                            ) : (
                                <div className="w-20 h-20 flex items-center justify-center border border-zinc-800 shrink-0">
                                    <Gift size={28} className="text-zinc-700" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="font-bold text-sm truncate">{r.name}</div>
                                    {!r.active && <span className="chip border-zinc-700 text-zinc-500">Inactive</span>}
                                </div>
                                <div className="text-[11px] text-orange-500 font-bold uppercase tracking-widest">{r.points_cost} pts</div>
                                {r.description && (
                                    <div className="text-[11px] text-zinc-500 line-clamp-2 mt-1">{r.description}</div>
                                )}
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => setEditing(r)} data-testid={`reward-catalog-edit-${r.id}`} className="text-[11px] uppercase tracking-widest text-orange-500 hover:underline">
                                        <Pencil size={12} className="inline" /> Modifier
                                    </button>
                                    <button onClick={() => remove(r.id)} data-testid={`reward-catalog-delete-${r.id}`} className="text-[11px] uppercase tracking-widest text-red-400 hover:underline">
                                        <Trash2 size={12} className="inline" /> Supprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <RewardCatalogEditor
                    item={editing === "new" ? null : editing}
                    onClose={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}

function RewardCatalogEditor({ item, onClose }) {
    const [name, setName] = useState(item?.name || "");
    const [description, setDescription] = useState(item?.description || "");
    const [pointsCost, setPointsCost] = useState(item?.points_cost || 100);
    const [imagePath, setImagePath] = useState(item?.image_path || "");
    const [active, setActive] = useState(item?.active ?? true);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImagePath(data.path);
            toast.success("Image téléchargée");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setUploading(false); }
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Nom requis"); return; }
        if (!pointsCost || pointsCost <= 0) { toast.error("Coût en points invalide"); return; }
        setSaving(true);
        try {
            const payload = { name, description, points_cost: +pointsCost, image_path: imagePath || null, active };
            if (item) await api.put(`/admin/reward-catalog/${item.id}`, payload);
            else await api.post("/admin/reward-catalog", payload);
            toast.success("Enregistré");
            onClose();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={onClose}
            data-testid="reward-catalog-editor"
        >
            <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-display text-2xl uppercase tracking-tight">
                    {item ? "Modifier" : "Nouvelle"} récompense
                </h3>
                <div>
                    <label className="label-tu">Nom</label>
                    <input data-testid="reward-name" value={name} onChange={(e) => setName(e.target.value)} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Description</label>
                    <textarea data-testid="reward-description-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Coût (points)</label>
                    <input data-testid="reward-cost" type="number" min={1} value={pointsCost} onChange={(e) => setPointsCost(e.target.value)} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Image (200 × 200 px conseillé)</label>
                    <div className="flex items-center gap-3">
                        {imagePath ? (
                            <img src={fileUrl(imagePath)} alt="" className="w-20 h-20 object-cover border border-zinc-800" />
                        ) : (
                            <div className="w-20 h-20 flex items-center justify-center border border-dashed border-zinc-700">
                                <Gift size={24} className="text-zinc-700" />
                            </div>
                        )}
                        <label className="btn-ghost py-2 px-4 text-xs cursor-pointer">
                            {uploading ? "Téléchargement…" : "Choisir un fichier"}
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                className="hidden"
                                data-testid="reward-image-upload"
                                onChange={(e) => upload(e.target.files?.[0])}
                            />
                        </label>
                        {imagePath && (
                            <button type="button" onClick={() => setImagePath("")} className="text-xs text-red-400 hover:underline">
                                Retirer
                            </button>
                        )}
                    </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input data-testid="reward-active" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                    Active (visible par les clubs)
                </label>
                <div className="flex gap-2 pt-2">
                    <button data-testid="reward-save" onClick={save} disabled={saving} className="btn-primary flex-1 py-2 text-sm">
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                    <button onClick={onClose} className="btn-ghost py-2 px-4 text-sm">Annuler</button>
                </div>
            </div>
        </div>
    );
}

function Rewards({ onChange }) {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const params = {};
        if (q) params.q = q;
        if (filter) params.status = filter;
        const { data } = await api.get("/admin/rewards", { params });
        setItems(data.items || []);
        setCounts(data.counts || {});
        onChange?.();
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [q, filter]);

    const STATUS_CFG = {
        pending: { cls: "border-orange-500/40 text-orange-500", l: "À préparer" },
        preparing: { cls: "border-yellow-700 text-yellow-400", l: "En préparation" },
        shipped: { cls: "border-cyan-700 text-cyan-400", l: "Expédié" },
        delivered: { cls: "border-green-700 text-green-400", l: "Livré" },
        cancelled: { cls: "border-red-900 text-red-400", l: "Annulé" },
    };

    const exportCsv = () => {
        const rows = [["Club", "Ville", "Palier", "Récompense", "Statut", "Tracking", "Adresse"]];
        items.forEach((c) => rows.push([
            c.club_name, c.club_city, `${c.threshold} pts`,
            c.reward_label, c.status, c.tracking_number || "", c.shipping_address || "",
        ]));
        const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "sponsormania-recompenses.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    if (editing) return (
        <AdminModal onClose={() => { setEditing(null); reload(); }} testid="reward-modal" maxWidth="max-w-3xl">
            <RewardDetail claim={editing} statusCfg={STATUS_CFG} onClose={() => { setEditing(null); reload(); }} />
        </AdminModal>
    );

    return (
        <div className="space-y-4">
            <div className="card-tu border-l-4 border-l-orange-500 flex items-start gap-3">
                <Gift className="text-orange-500 shrink-0" />
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                        Suivi des récompenses fidélité club
                    </div>
                    <p className="text-zinc-300 text-sm">
                        Les clubs cumulent des points et choisissent eux-mêmes les récompenses dans la boutique fidélité.
                        Préparez et expédiez le colis, renseignez le numéro de suivi et marquez livré une fois reçu.
                        La configuration du catalogue se fait dans l'onglet Config.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[260px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="rewards-search"
                        placeholder="Rechercher par nom de club…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="input-tu pl-9"
                    />
                </div>
                <div className="flex flex-wrap gap-px bg-zinc-800 border border-zinc-800">
                    {[
                        { v: "", l: `Tous (${counts.total || 0})` },
                        { v: "pending", l: `À préparer (${counts.pending || 0})` },
                        { v: "preparing", l: `Préparation (${counts.preparing || 0})` },
                        { v: "shipped", l: `Expédiés (${counts.shipped || 0})` },
                        { v: "delivered", l: `Livrés (${counts.delivered || 0})` },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setFilter(f.v)}
                            data-testid={`rewards-filter-${f.v || "all"}`}
                            className={[
                                "px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors",
                                filter === f.v
                                    ? "bg-gradient-to-r from-orange-600 to-red-600 text-white"
                                    : "bg-zinc-950 text-zinc-400 hover:text-white",
                            ].join(" ")}
                        >{f.l}</button>
                    ))}
                </div>
                <button onClick={exportCsv} data-testid="rewards-export" className="btn-ghost py-2 px-4 text-xs">
                    Exporter CSV
                </button>
            </div>

            {items.length === 0 ? (
                <div className="card-tu text-center py-16 text-zinc-400">
                    <Gift size={28} className="text-zinc-600 mx-auto mb-3" />
                    Aucune récompense pour ce filtre.
                </div>
            ) : (
                <div className="card-tu overflow-x-auto p-0">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                                <Th>Club</Th>
                                <Th>Récompense</Th>
                                <Th>Adresse</Th>
                                <Th>Tracking</Th>
                                <Th>Statut</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => {
                                const s = STATUS_CFG[c.status] || { cls: "", l: c.status };
                                return (
                                    <tr
                                        key={c.id}
                                        data-testid={`reward-row-${c.id}`}
                                        onClick={() => setEditing(c)}
                                        className={[
                                            "border-b border-zinc-900 cursor-pointer hover:bg-zinc-900/30",
                                            c.status === "pending" ? "bg-orange-950/10" : "",
                                        ].join(" ")}
                                    >
                                        <Td>
                                            <div className="font-bold text-orange-500 underline-offset-2 hover:underline">{c.club_name}</div>
                                            <div className="text-xs text-zinc-500">
                                                {c.club_city} · {c.club_department}
                                            </div>
                                        </Td>
                                        <Td>
                                            <div className="flex items-center gap-2">
                                                <Gift size={14} className="text-orange-500" />
                                                <div>
                                                    <div className="font-bold">{c.reward_label}</div>
                                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                                                        Palier {c.threshold} pts
                                                    </div>
                                                </div>
                                            </div>
                                        </Td>
                                        <Td className="text-xs">
                                            {c.shipping_address ? (
                                                <span className="text-zinc-300">{c.shipping_address}</span>
                                            ) : (
                                                <span className="text-zinc-600 italic">Non renseignée</span>
                                            )}
                                        </Td>
                                        <Td className="text-xs font-mono">
                                            {c.tracking_number || <span className="text-zinc-600">—</span>}
                                        </Td>
                                        <Td><span className={`chip ${s.cls}`}>{s.l}</span></Td>
                                        <Td>
                                            <button
                                                data-testid={`reward-open-${c.id}`}
                                                onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                                                className="text-xs uppercase tracking-widest text-orange-500 hover:underline inline-flex items-center gap-1"
                                            >
                                                <Truck size={12} /> Gérer
                                            </button>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
